import { authClient } from "@d12labs/auth/client";
import { Button } from "@d12labs/ui/button";
import { Input } from "@d12labs/ui/input";
import { Label } from "@d12labs/ui/label";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { AuthLayout } from "@/components/pages/auth-layout";

export function ForgotPasswordPage() {
	const [email, setEmail] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [sent, setSent] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setSubmitting(true);
		const { error: err } = await authClient.requestPasswordReset({
			email,
			redirectTo: `${window.location.origin}/reset-password`,
		});
		setSubmitting(false);
		if (err) {
			setError(err.message ?? "Request failed");
			return;
		}
		setSent(true);
	};

	if (sent) {
		return (
			<AuthLayout
				title="Check your email"
				description={`If an account exists for ${email}, a reset link is on its way.`}
				footer={
					<Link to="/login" className="underline">
						Back to sign in
					</Link>
				}
			>
				<p className="text-sm text-muted-foreground">
					The link expires in an hour. You can close this tab — the reset page opens straight from
					the email.
				</p>
			</AuthLayout>
		);
	}

	return (
		<AuthLayout
			title="Forgot password"
			description="Enter your email and we'll send a reset link."
			footer={
				<>
					Remembered it?{" "}
					<Link to="/login" className="underline">
						Sign in
					</Link>
				</>
			}
		>
			<form onSubmit={handleSubmit} className="flex flex-col gap-4">
				<div className="flex flex-col gap-2">
					<Label htmlFor="email">Email</Label>
					<Input
						id="email"
						type="email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						required
						autoComplete="email"
						autoFocus
					/>
				</div>
				{error && <p className="text-sm text-destructive">{error}</p>}
				<Button type="submit" disabled={submitting}>
					{submitting ? "Sending…" : "Send reset link"}
				</Button>
			</form>
		</AuthLayout>
	);
}
